// react-redux-toastr moves focus on its own: ToastrBox focuses its close button on mount (a toast opts out
// of that with `disableCloseButtonFocus`, and only the notification one does) and, on unmount, focuses the
// close button of whatever toast is left on screen — that one runs in a setTimeout and has no opt-out. Any
// of the ~30 toastr calls in the app can therefore pull the caret out of a field the user is typing in.
// Mounted once, from the app root. (EPN-3051)

const gestureEvents = ['mousedown', 'touchstart', 'keydown'];
const gestureGraceTime = 150;
// what the user gets put back into after a toast jumps the focus
const focusRestoreSelector = 'input, textarea, [contenteditable=""], [contenteditable="true"]';

let lastFocusGestureAt = 0;
let previouslyFocused = null;

// only a press or Tab moves focus on purpose, plain typing never does
function markFocusGesture(e) {
  if (e.type === 'keydown' && e.key !== 'Tab') return;
  lastFocusGestureAt = Date.now();
}

function isRestorable(node) {
  if (!node || typeof node.matches !== 'function') return false;
  return node.matches(focusRestoreSelector) && document.body.contains(node);
}

function isToastControl(node) {
  return !!node && typeof node.closest === 'function' && !!node.closest('.close-toastr');
}

// Restore after the in-flight focus change has settled rather than from inside the handler: re-entrant
// focus moves during event dispatch are a known hazard, and this way we can also re-check that the toast
// still holds the caret, so a focus move that legitimately happened in between is left alone.
function restoreFocus(node) {
  Promise.resolve().then(() => {
    if (!isToastControl(document.activeElement)) return;
    if (!isRestorable(node)) return;
    node.focus();
  });
}

function handleFocusIn(e) {
  const { target, relatedTarget } = e;
  // Chrome does fill relatedTarget in; previouslyFocused is the fallback for when it comes through empty
  const cameFrom = relatedTarget || previouslyFocused;
  previouslyFocused = target || null;

  if (!isToastControl(target)) return;
  if (Date.now() - lastFocusGestureAt < gestureGraceTime) return;
  if (!isRestorable(cameFrom)) return;

  restoreFocus(cameFrom);
}

export function initToastrFocusGuard() {
  gestureEvents.forEach((name) => document.addEventListener(name, markFocusGesture, true));
  document.addEventListener('focusin', handleFocusIn, true);

  return () => {
    gestureEvents.forEach((name) => document.removeEventListener(name, markFocusGesture, true));
    document.removeEventListener('focusin', handleFocusIn, true);
    previouslyFocused = null;
  };
}
